/**
 * EventCalendarRow
 *
 * Calendar selector showing account information.
 * Displays which calendar the event will be saved to, with account email (Android).
 * Shows assigned family member pills beneath the calendar name.
 */

import { useMemo } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';
import type { CalendarMemberMapping } from '@/types/calendar-member-mapping';
import type { FamilyMember } from '@/types/family-member';

type EventCalendarRowProps = {
  calendars: DeviceCalendar[];
  selectedId: string | null;
  onPress: () => void;
  palette: typeof Colors.light;
  mode: 'create' | 'edit';
  calendarMappings?: CalendarMemberMapping[];
  familyMembers?: FamilyMember[];
};

export function EventCalendarRow({
  calendars,
  selectedId,
  onPress,
  palette,
  mode,
  calendarMappings = [],
  familyMembers = [],
}: EventCalendarRowProps) {
  const selectedCalendar = calendars.find(c => c.id === selectedId);

  // Display format: "Calendar Name (account@email.com)" or "Calendar Name" if no account
  const calendarName = selectedCalendar?.title
    ?? (mode === 'create' ? 'Select calendar' : '');
  const accountInfo = selectedCalendar?.ownerAccount
    ? ` (${selectedCalendar.ownerAccount})`
    : '';
  const fullDisplayName = `${calendarName}${accountInfo}`;

  const isEditable = mode === 'create';
  const Container = isEditable ? TouchableOpacity : View;
  const containerProps = isEditable ? { onPress } : {};

  // Get family members assigned to the selected calendar
  const assignedMembers = useMemo(() => {
    if (!selectedId || calendarMappings.length === 0 || familyMembers.length === 0) {
      return [];
    }

    const mapping = calendarMappings.find(m => m.calendarId === selectedId);
    if (!mapping) return [];

    return familyMembers.filter(member => mapping.memberIds.includes(member.id));
  }, [selectedId, calendarMappings, familyMembers]);

  return (
    <Container style={styles.container} {...containerProps}>
      <View style={styles.iconContainer}>
        <Ionicons name="calendar-outline" size={18} color={palette.textMuted} />
      </View>
      <View style={styles.content}>
        <Text style={[styles.label, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
          On calendar
        </Text>
        {assignedMembers.length > 0 && (
          <View style={styles.assignedMembersRow}>
            {assignedMembers.map(member => (
              <View
                key={member.id}
                style={[styles.memberPill, { backgroundColor: member.color }]}
              >
                <View style={styles.pillDot} />
                <Text style={[styles.pillText, { fontFamily: getBodyFont() }]}>
                  {member.name}
                </Text>
              </View>
            ))}
          </View>
        )}
        <Text
          style={[styles.value, { color: palette.text, fontFamily: getBodyFont() }]}
          numberOfLines={1}
        >
          {fullDisplayName}
        </Text>
      </View>
      {isEditable && (
        <Ionicons name="chevron-forward" size={16} color={palette.textMuted} />
      )}
    </Container>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md + 2, // 14px
  },
  iconContainer: {
    width: 40,
    alignItems: 'center',
    paddingTop: 2,
  },
  content: {
    flex: 1,
    gap: 2,
  },
  label: {
    fontSize: 14,
  },
  value: {
    fontSize: 14,
  },
  assignedMembersRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignSelf: 'flex-start',
    gap: Spacing.xs,
    marginTop: 2,
    marginBottom: 2,
  },
  memberPill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: Spacing.sm,
    borderRadius: BorderRadius.md,
    gap: 6,
  },
  pillDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#FFFFFF',
  },
  pillText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#FFFFFF',
  },
});
